import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import CategoryButton from "../components/CategoryButton";
import ListingCard from "../components/ListingCard";
import { getCategories, getProducts } from "../utils/api";

function normCat(c) {
  return {
    id: c?.id,
    label: c?.title ?? c?.label ?? "Category",
    image_path: c?.image_path ?? c?.image ?? "",
  };
}

export default function MyListings({ currentUser }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialCat = params.get("cat");

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  const [activeCatId, setActiveCatId] = useState(initialCat || "all");
  const [loading, setLoading] = useState(true);

  // local search
  const [q, setQ] = useState("");

  useEffect(() => {
    setActiveCatId(initialCat || "all");
  }, [initialCat]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        const [cats, prods] = await Promise.all([
          getCategories(),
          getProducts(),
        ]);
        if (cancelled) return;

        setCategories(Array.isArray(cats) ? cats.map(normCat) : []);
        setProducts(Array.isArray(prods) ? prods : []);
      } catch {
        if (!cancelled) {
          setCategories([]);
          setProducts([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  function setCat(val, doNavigate = true) {
    const v = String(val || "all");
    setActiveCatId(v);

    if (doNavigate) {
      navigate(v === "all" ? "/my-listings" : `/my-listings?cat=${v}`);
    }
  }

  const isLoggedIn = Boolean(currentUser);
  const role = String(currentUser?.role || "").toLowerCase();
  const isSellerOrAdmin = role === "seller" || role === "admin";

  const mine = useMemo(() => {
    if (!currentUser?.id) return [];
    const myId = String(currentUser.id);

    return (Array.isArray(products) ? products : [])
      .filter(Boolean)
      .filter((p) => {
        const seller = p?.seller_user_id ?? p?.user_id ?? "";
        return String(seller) === myId && p?.id != null;
      });
  }, [products, currentUser]);

  const filtered = useMemo(() => {
    let list = Array.isArray(mine) ? mine : [];

    if (activeCatId && activeCatId !== "all") {
      const target = String(activeCatId);
      list = list.filter(
        (p) =>
          String(
            p?.product_category_id ?? p?.category_id ?? p?.categoryId ?? "",
          ) === target,
      );
    }

    const query = String(q || "")
      .trim()
      .toLowerCase();
    if (query) {
      list = list.filter((p) =>
        String(p?.name ?? p?.title ?? "")
          .toLowerCase()
          .includes(query),
      );
    }

    return list;
  }, [mine, activeCatId, q]);

  const activeCatLabel = useMemo(() => {
    if (activeCatId === "all") return "All my listings";
    const found = categories.find((c) => String(c.id) === String(activeCatId));
    return found?.label ? `${found.label} listings` : "Listings";
  }, [activeCatId, categories]);

  if (!isLoggedIn) {
    return (
      <div
        className="container pagePadBottom shopPage"
        style={{ maxWidth: 720 }}
      >
        <div className="card">
          <div className="shopTitle">My Listings</div>
          <div className="shopSubtitle">Login first.</div>
          <div className="row" style={{ marginTop: 12 }}>
            <Link className="btnPrimary" to="/login">
              Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!isSellerOrAdmin) {
    return (
      <div
        className="container pagePadBottom shopPage"
        style={{ maxWidth: 720 }}
      >
        <div className="card">
          <div className="shopTitle">My Listings</div>
          <div className="shopSubtitle">Only sellers can manage listings.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container pagePadBottom shopPage">
      <div className="shopHeader">
        <div className="shopHeadLeft">
          <div className="shopTitle">My Listings</div>
          <div className="shopSubtitle">
            Your advertisements — filter by category, then search.
          </div>
        </div>

        <div className="row" style={{ gap: 10 }}>
          <span className="shopCount">
            {loading ? "…" : `${filtered.length} item(s)`}
          </span>
          <Link className="btnPrimary" to="/add-item">
            Add item
          </Link>
        </div>
      </div>

      <div className="shopControls">
        <div className="shopControl shopControlSearch">
          <div className="label">Search</div>
          <div className="shopSearchWrap">
            <input
              className="input shopSearch"
              placeholder="Search my listings…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            {String(q || "").trim() ? (
              <button
                type="button"
                className="shopClearBtn"
                onClick={() => setQ("")}
                aria-label="Clear search"
              >
                ✕
              </button>
            ) : null}
          </div>
        </div>

        {/* Mobile dropdown */}
        <div className="shopControl shopControlSelect">
          <div className="label">Category</div>
          <select
            className="input shopSelect"
            value={activeCatId}
            onChange={(e) => setCat(e.target.value)}
          >
            <option value="all">All categories</option>
            {categories.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Pills row (desktop) */}
      <div className="shopPills">
        <div className="catRow">
          <Link
            className={`catBtn ${activeCatId === "all" ? "catBtnActive" : ""}`}
            to="/my-listings"
            onClick={(e) => {
              e.preventDefault();
              setCat("all");
            }}
            title="All categories"
          >
            <span className="catDot" aria-hidden="true" />
            <span className="catBtnLabel">All</span>
          </Link>

          {categories.map((c) => (
            <CategoryButton
              key={c.id}
              cat={c}
              to={`/my-listings?cat=${c.id}`}
              active={String(activeCatId) === String(c.id)}
              onClick={() => setCat(String(c.id), false)}
            />
          ))}
        </div>
      </div>

      <div className="shopSectionBar">
        <div className="shopSectionTitle">{activeCatLabel}</div>
        <div className="muted small">
          Tap a listing to manage or view details.
        </div>
      </div>

      {loading ? (
        <div className="shopHint">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="emptyBox">
          <div className="emptyTitle">No listings found</div>
          <div className="muted small" style={{ marginTop: 6 }}>
            Try changing category or search keywords.
          </div>
        </div>
      ) : (
        <div className="grid3 shopGrid">
          {filtered.map((item) => (
            <ListingCard key={String(item.id)} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}
